import {ChangeEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {KeyAsString} from 'type-fest';
import {useDebounce} from 'use-debounce';
import {uniq} from 'lodash';
import {TreeNodeProps} from '@ennabl/rc-tree';

import {FuseSearchKey, useFuzzySearch} from 'utils/hooks';
import {filterTreeByMatchKeys, GenericTreeNode} from 'utils/treeUtils';

import {TreeKey, TreeBasicNode} from 'components/Tree/TreeTypes';
import {SearchSwitcherIcon} from 'components/Tree/TreeSwitcher';

type ItemWithParent<T> = T & {parentKey?: TreeKey};

const defaultKeys: KeyAsString<TreeBasicNode>[] = ['title'];

export function useTreeSearch<K extends TreeKey, T extends TreeBasicNode<K>>(
  tree: T[],
  searchQuery: string,
  keys: FuseSearchKey<T[]>[] = defaultKeys as any
): {
  matchKeys: Set<K>;
  matchParentKeys: Set<K>;
  hasSearchQuery: boolean;
  isNothingFound: boolean;
} {
  const hasSearchQuery = searchQuery.trim().length > 0;
  const {list, map} = useMemo(() => {
    if (!hasSearchQuery) {
      return {list: [] as ItemWithParent<T>[], map: new Map<K, ItemWithParent<T>>()};
    }

    const list = flattenTree(tree);
    const map = new Map(list.map(item => [item.key, item]));

    return {list, map};
  }, [hasSearchQuery, tree]);

  const filteredList = useFuzzySearch(
    list,
    searchQuery,
    useMemo(() => ({keys}), [keys])
  );

  return useMemo(() => {
    if (!hasSearchQuery) {
      return {
        matchKeys: new Set(),
        matchParentKeys: new Set(),
        hasSearchQuery,
        isNothingFound: false,
      };
    }

    const matchKeys = new Set(filteredList.map(item => item.key));
    const matchParentKeys = new Set(uniq(filteredList.flatMap(item => getParentChain(map, item.parentKey))));
    const isNothingFound = matchKeys.size === 0;

    return {
      isNothingFound,
      matchKeys,
      matchParentKeys,
      hasSearchQuery,
    };
  }, [filteredList, hasSearchQuery, map]);
}

function flattenTree<K extends TreeKey, T extends GenericTreeNode<TreeBasicNode<K>>>(
  tree: T[],
  parentKey?: K
): ItemWithParent<T>[] {
  return tree.flatMap(item => {
    const itemWithParent = {...item, parentKey};

    if (!item.children) {
      return [itemWithParent];
    }

    return [itemWithParent, ...(flattenTree(item.children, item.key) as ItemWithParent<T>[])];
  });
}

function getParentChain<K extends TreeKey, T extends TreeBasicNode<K>>(map: Map<K, ItemWithParent<T>>, key?: K): K[] {
  if (!key) {
    return [];
  }

  const parentKey = map.get(key)?.parentKey;

  if (parentKey) {
    return [...getParentChain(map, parentKey), key];
  }

  return [key];
}

type UseTreeSearchProps<K extends TreeKey, T extends TreeBasicNode<K>> = {
  treeData: T[];
  treeMap?: Map<K, TreeBasicNode<K>>;
  searchKeys?: FuseSearchKey<T[]>[];
  initialSearchQuery?: string;
  initialExpandedKeys?: K[];
};

const switcherIconRenderer = (props: TreeNodeProps) => <SearchSwitcherIcon {...props} />;

export function useTreeSearchProps<K extends TreeKey, T extends TreeBasicNode<K>>({
  treeData,
  treeMap,
  searchKeys,
  initialExpandedKeys,
  initialSearchQuery,
}: UseTreeSearchProps<K, T>) {
  const [internalInitialData, setInternalInitialData] = useState<
    {searchQuery?: string; expandedKeys?: K[]} | undefined
  >(() => ({
    searchQuery: initialSearchQuery,
    expandedKeys: initialExpandedKeys,
  }));

  const [searchQuery, setSearchQuery] = useState(initialSearchQuery ?? '');
  const [searchQueryDebounced] = useDebounce(searchQuery, 300);
  const [internalInitialDataDebounced] = useDebounce(internalInitialData, 300);

  const [expandedKeys, setExpandedKeys] = useState<K[]>(initialExpandedKeys ?? []);
  const prevSearchQueryRef = useRef(searchQueryDebounced);
  const {matchParentKeys, matchKeys, hasSearchQuery, isNothingFound} = useTreeSearch<K, T>(
    treeData,
    searchQueryDebounced,
    searchKeys
  );

  useEffect(() => {
    if (initialSearchQuery || initialExpandedKeys) {
      setInternalInitialData({
        searchQuery: initialSearchQuery,
        expandedKeys: initialExpandedKeys,
      });
      setSearchQuery(initialSearchQuery ?? '');
    }
  }, [initialSearchQuery, initialExpandedKeys]);

  useEffect(() => {
    const searchQueryChanged = prevSearchQueryRef.current !== searchQueryDebounced;
    prevSearchQueryRef.current = searchQueryDebounced;

    if (
      internalInitialDataDebounced?.searchQuery === searchQueryDebounced &&
      !!internalInitialDataDebounced?.expandedKeys?.length
    ) {
      return setExpandedKeys(internalInitialDataDebounced.expandedKeys);
    }

    if (hasSearchQuery) {
      setExpandedKeys([...matchParentKeys]);
    } else if (searchQueryChanged) {
      // Collapse only when the search is actually cleared, not on incidental
      // re-renders (e.g. a parent re-render that gives `treeData` a new identity).
      // Otherwise manual expansion would be wiped on every unrelated update.
      setExpandedKeys([]);
    }
  }, [hasSearchQuery, matchParentKeys, searchQueryDebounced, internalInitialDataDebounced]);

  const onSearchChange = useCallback((_: ChangeEvent<HTMLInputElement> | undefined, value: string) => {
    setSearchQuery(value);
    setInternalInitialData(undefined);
  }, []);
  const onTextFieldChange = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearchQuery(event.target.value);
    setInternalInitialData(undefined);
  }, []);

  const handleExpand = useCallback(
    (keys: K[]) => {
      if (treeMap) {
        setExpandedKeys(keys.filter(key => !!treeMap?.get(key)?.childrenCount));
      } else {
        setExpandedKeys(keys);
      }
    },
    [treeMap]
  );

  const treeProps = useMemo(() => {
    return {
      expandedKeys: expandedKeys,
      onExpand: handleExpand,
      searchQuery: searchQueryDebounced,
      switcherIcon: hasSearchQuery ? switcherIconRenderer : undefined,
    };
  }, [expandedKeys, hasSearchQuery, searchQueryDebounced, handleExpand]);

  const searchProps = useMemo(() => {
    return {
      value: searchQuery,
      onChange: onSearchChange,
    };
  }, [searchQuery, onSearchChange]);

  const textFieldProps = useMemo(() => {
    return {
      value: searchQuery,
      onChange: onTextFieldChange,
    };
  }, [searchQuery, onTextFieldChange]);

  const filteredTree = useMemo(() => filterTreeByMatchKeys(treeData, matchKeys), [treeData, matchKeys]);

  return {
    treeProps,
    searchProps,
    textFieldProps,
    isNothingFound,
    filteredTree,
  };
}
